import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Property } from '@/types/property';

export interface RankedListing {
  id: string;
  community_id: string;
  property_id: string;
  criteria_id: string;
  rank: number;
  score_total: number;
  score_breakdown: {
    price: { score: number; reason: string };
    photos: { score: number; reason: string };
    recency: { score: number; reason: string };
    completeness: { score: number; reason: string };
  };
  computed_at: string;
  property?: Property;
}

export interface RankingCriteria {
  id: string;
  name: string;
  is_active: boolean;
  weight_price: number;
  weight_photos: number;
  weight_recency: number;
  weight_completeness: number;
  min_photos: number;
  max_days_old: number;
  include_property_types: string[];
  exclude_keywords: string[];
  created_at: string;
  updated_at: string;
}

export interface ComputeLog {
  id: string;
  criteria_id: string | null;
  communities_updated: number;
  listings_ranked: number;
  status: string;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export function useRankedListings(communityId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ['ranked-listings', communityId, limit],
    queryFn: async () => {
      if (!communityId) return [];

      const { data, error } = await supabase
        .from('community_listing_ranks')
        .select(`
          *,
          property:properties(*)
        `)
        .eq('community_id', communityId)
        .order('rank', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data as unknown as RankedListing[];
    },
    enabled: !!communityId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useRankedListingsBySlug(slug: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ['ranked-listings-by-slug', slug, limit],
    queryFn: async () => {
      if (!slug) return [];

      // First get community by slug
      const { data: community, error: commError } = await supabase
        .from('communities')
        .select('id')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (commError || !community) return [];

      const { data, error } = await supabase
        .from('community_listing_ranks')
        .select(`
          *,
          property:properties(*)
        `)
        .eq('community_id', community.id)
        .order('rank', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data as unknown as RankedListing[];
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// Convert neighborhood name to slug format
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');
}

export function useRankedListingsByNeighborhood(neighborhoodName: string | undefined, limit = 6) {
  const slug = neighborhoodName ? toSlug(neighborhoodName) : undefined;
  
  return useQuery({
    queryKey: ['ranked-listings-by-neighborhood', neighborhoodName, limit],
    queryFn: async () => {
      if (!slug) return [];

      // Find community by matching slug or name
      const { data: community, error: commError } = await supabase
        .from('communities')
        .select('id, name, slug')
        .eq('is_active', true)
        .or(`slug.eq.${slug},name.ilike.${neighborhoodName}`)
        .limit(1)
        .maybeSingle();

      if (commError || !community) return [];

      const { data, error } = await supabase
        .from('community_listing_ranks')
        .select(`
          *,
          property:properties(*)
        `)
        .eq('community_id', community.id)
        .order('rank', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data as unknown as RankedListing[];
    },
    enabled: !!neighborhoodName,
    staleTime: 5 * 60 * 1000,
  });
}
// Slim column set used by card displays — avoids fetching `description` and
// `features` arrays which can be 10-50KB per row.
const PROPERTY_CARD_COLS =
  'id, slug, title, address, city, neighborhood, price, bedrooms, bathrooms, sqft, property_type, images, listing_date, latitude, longitude, status, agent_name, brokerage_name';

export function useGlobalTopRanked(limit = 6, city?: string) {
  return useQuery({
    queryKey: ['global-top-ranked', limit, city],
    queryFn: async () => {
      // Fetch a modest pool, then filter by city on the client. 30 rows is
      // plenty to find `limit` city matches without ballooning payload size.
      const { data, error } = await supabase
        .from('community_listing_ranks')
        .select(`
          id, rank, score_total, community_id, property_id,
          property:properties(${PROPERTY_CARD_COLS}),
          community:communities(name, slug, city)
        `)
        .order('score_total', { ascending: false })
        .limit(city ? 40 : limit);

      if (error) throw error;
      
      let results = data as unknown as (RankedListing & { community: { name: string; slug: string; city: string | null } })[];
      
      // Filter by city if provided
      if (city) {
        results = results.filter(r => 
          r.property?.city?.toLowerCase() === city.toLowerCase() ||
          r.community?.city?.toLowerCase() === city.toLowerCase()
        );
      }
      
      return results.slice(0, limit);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useTopRankedByCommunities(limit = 3, city?: string) {
  return useQuery({
    queryKey: ['top-ranked-communities', limit, city],
    queryFn: async () => {
      // Get all communities with their top ranked listings
      let query = supabase
        .from('communities')
        .select('*')
        .eq('is_active', true)
        .order('name');

      // Filter by city if provided
      if (city) {
        query = query.ilike('city', city);
      }

      const { data: communities, error: commError } = await query;

      if (commError) throw commError;

      const result: { community: any; listings: RankedListing[] }[] = [];

      for (const community of communities || []) {
        const { data: rankings } = await supabase
          .from('community_listing_ranks')
          .select(`
            *,
            property:properties(*)
          `)
          .eq('community_id', community.id)
          .order('rank', { ascending: true })
          .limit(limit);

        if (rankings && rankings.length > 0) {
          result.push({ community, listings: rankings as unknown as RankedListing[] });
        }
      }

      return result;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useRankingCriteria() {
  return useQuery({
    queryKey: ['ranking-criteria'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ranking_criteria')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as RankingCriteria[];
    },
  });
}

export function useActiveRankingCriteria() {
  return useQuery({
    queryKey: ['active-ranking-criteria'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ranking_criteria')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as RankingCriteria;
    },
  });
}

export function useCreateRankingCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (criteria: { name: string; weight_price?: number; weight_photos?: number; weight_recency?: number; weight_completeness?: number }) => {
      const { data, error } = await supabase
        .from('ranking_criteria')
        .insert([criteria])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-criteria'] });
    },
  });
}

export function useUpdateRankingCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<RankingCriteria> & { id: string }) => {
      const { data, error } = await supabase
        .from('ranking_criteria')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-criteria'] });
      queryClient.invalidateQueries({ queryKey: ['active-ranking-criteria'] });
    },
  });
}

export function useSetActiveCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (criteriaId: string) => {
      // Deactivate all criteria
      await supabase
        .from('ranking_criteria')
        .update({ is_active: false })
        .neq('id', 'placeholder');

      // Activate the selected one
      const { data, error } = await supabase
        .from('ranking_criteria')
        .update({ is_active: true })
        .eq('id', criteriaId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-criteria'] });
      queryClient.invalidateQueries({ queryKey: ['active-ranking-criteria'] });
    },
  });
}

export function useComputeRankings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (communityIds?: string[]) => {
      const { data, error } = await supabase.functions.invoke('compute-rankings', {
        body: { communityIds },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranked-listings'] });
      queryClient.invalidateQueries({ queryKey: ['ranked-listings-by-slug'] });
      queryClient.invalidateQueries({ queryKey: ['top-ranked-communities'] });
      queryClient.invalidateQueries({ queryKey: ['compute-logs'] });
    },
  });
}

export function useComputeLogs() {
  return useQuery({
    queryKey: ['compute-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ranking_compute_log')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as ComputeLog[];
    },
  });
}
